import { isDefined } from "@clipboard-health/util-ts";
import { startOfDay, subDays } from "date-fns";

import { type ResolvedCancellationProtectionPayout } from "./types";
import { usePaginatedCancellationProtectionPayouts } from "./usePaginatedCancellationProtectionPayouts";

const NEARBY_PAYOUT_PAGE_SIZE = 25;

interface UseNearbyCancellationProtectionPayoutsOptions {
  lookbackDays: number;
  workplaceMsa?: string;
  enabled?: boolean;
}

interface UseNearbyCancellationProtectionPayoutsResult {
  payouts: ResolvedCancellationProtectionPayout[];
  totalCountLabel: string;
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
}

export function useNearbyCancellationProtectionPayouts(
  options: UseNearbyCancellationProtectionPayoutsOptions
): UseNearbyCancellationProtectionPayoutsResult {
  const { lookbackDays, workplaceMsa, enabled = true } = options;

  const {
    data: payoutsData,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = usePaginatedCancellationProtectionPayouts({
    params: {
      filter: {
        createdAt: {
          gte: [startOfDay(subDays(new Date(), lookbackDays)).toISOString()],
        },
        "workplace.msa": { eq: workplaceMsa },
      },
      sort: ["-createdAt"],
      page: { size: NEARBY_PAYOUT_PAGE_SIZE },
    },
    enabled: enabled && isDefined(workplaceMsa) && workplaceMsa.length > 0,
  });

  const payouts = payoutsData?.pages.flat() ?? [];

  const defaultedHasNextPage = hasNextPage ?? false;
  const totalCountLabel = defaultedHasNextPage ? `${payouts.length}+` : `${payouts.length}`;

  return {
    payouts,
    totalCountLabel,
    isLoading,
    hasNextPage: defaultedHasNextPage,
    isFetchingNextPage,
    fetchNextPage: async () => {
      await fetchNextPage();
    },
  };
}
